import type { ApiResponse } from '@persona/shared';

/**
 * Клиент админского API.
 *
 * Сессия администратора живёт в отдельных cookie с путём `/api/admin`,
 * поэтому она не пересекается с клиентской и не отправляется на её маршруты.
 */

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const CSRF_COOKIE = 'plk_admin_csrf';

function readCsrfToken(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`));
  return match ? decodeURIComponent(match[1]!) : null;
}

async function call<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const method = init.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') {
    const csrf = readCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  const response = await fetch(`/api/admin${path}`, {
    method,
    headers,
    credentials: 'same-origin',
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });

  const payload = (await response.json().catch(() => null)) as ApiResponse<T> | null;

  if (!payload) throw new ApiError('network_error', 'Сервис недоступен. Проверьте соединение.');
  if (!payload.ok) {
    const error = payload.error as { code: string; message: string; fields?: Record<string, string> };
    throw new ApiError(error.code, error.message, error.fields);
  }

  return payload.data;
}

export interface AdminProfile {
  id: string;
  role: 'viewer' | 'manager' | 'admin';
}

export interface AdminSummary {
  clients: number;
  withSubscription: number;
  newLast7Days: number;
  activeSubscriptions: number;
  /** Подписки, заканчивающиеся в ближайшую неделю. */
  expiringIn7Days: number;
  paidLast30Days: number;
  revenueLast30DaysMinor: number;
  failedPayments: number;
  /** Успешные платежи, не доехавшие до 1С: деньги списаны, продажи в учёте нет. */
  notPostedToOnec: number;
  /** Демонстрационные платежи. В выручку и в счётчик расхождений не входят. */
  demoRecords: number;
}

export interface AdminSubscriptionRow {
  id: string;
  client: { id: string; name: string; phone: string };
  tariffTitle: string;
  status: string;
  startedAt: string | null;
  currentPeriodEnd: string | null;
  daysLeft: number | null;
  autoRenew: boolean;
  nextChargeAt: string | null;
  nextChargeAmountMinor: number | null;
  failedChargeCount: number;
}

export interface AdminPaymentRow {
  id: string;
  client: { id: string; name: string; phone: string };
  kind: string;
  status: string;
  amountMinor: number;
  description: string;
  createdAt: string;
  paidAt: string | null;
  onecPostedAt: string | null;
  cardMask: string | null;
  errorMessage: string | null;
  /** Демонстрационная запись: денег не было, в 1С не передавалась. */
  isDemo: boolean;
}

export interface AdminClientDetail {
  id: string;
  phone: string;
  fullName: string | null;
  email: string | null;
  onecClientId: string | null;
  registeredAt: string;
  lastLoginAt: string | null;
  consents: Array<{ kind: string; docVersion: string; acceptedAt: string }>;
  memberships: Array<{
    title: string;
    status: string;
    endDate: string | null;
    isRecurrent: boolean;
    contractStatus: string | null;
    nextPaymentDate: string | null;
    nextPaymentAmountMinor: number | null;
    canCancel: boolean;
  }>;
  purchases: Array<{ id: string; datetime: string; totalMinor: number; debtMinor: number; items: string[] }>;
  payments: Array<{
    id: string;
    kind: string;
    status: string;
    amountMinor: number;
    createdAt: string;
    paidAt: string | null;
    onecPostedAt: string | null;
    isDemo: boolean;
  }>;
  onecError: string | null;
}

export interface AdminClientRow {
  id: string;
  phone: string;
  fullName: string | null;
  email: string | null;
  onecClientId: string | null;
  registeredAt: string;
  lastLoginAt: string | null;
  consents: string[];
  subscription: {
    status: string;
    tariffTitle: string;
    currentPeriodEnd: string | null;
    autoRenew: boolean;
  } | null;
}

export const adminApi = {
  login: (email: string, password: string) =>
    call<{ id: string; email: string; name: string; role: AdminProfile['role']; csrfToken: string }>('/auth/login', {
      method: 'POST',
      body: { email, password },
    }),

  logout: () => call<{ loggedOut: boolean }>('/auth/logout', { method: 'POST' }),

  me: () => call<AdminProfile>('/auth/me'),

  summary: () => call<AdminSummary>('/summary'),

  clients: (search: string, limit = 50, offset = 0) =>
    call<{ items: AdminClientRow[]; total: number }>(
      `/clients?search=${encodeURIComponent(search)}&limit=${limit}&offset=${offset}`,
    ),

  clientDetail: (id: string) => call<AdminClientDetail>(`/clients/${id}`),

  subscriptions: (search: string, limit = 50, offset = 0) =>
    call<{ items: AdminSubscriptionRow[]; total: number }>(
      `/subscriptions?search=${encodeURIComponent(search)}&limit=${limit}&offset=${offset}`,
    ),

  payments: (search: string, limit = 50, offset = 0) =>
    call<{ items: AdminPaymentRow[]; total: number }>(
      `/payments?search=${encodeURIComponent(search)}&limit=${limit}&offset=${offset}`,
    ),

  /** Возврат платежа. Сумма не указана — возвращаем всё, что ещё не вернули. */
  refund: (paymentId: string, reason: string, amountMinor?: number) =>
    call<{ refundedMinor: number; remainingMinor: number }>(`/payments/${paymentId}/refund`, {
      method: 'POST',
      body: { reason, amountMinor },
    }),

  audit: (limit = 100, offset = 0) =>
    call<{ items: AuditRow[]; total: number }>(`/audit?limit=${limit}&offset=${offset}`),
};

export interface AuditRow {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  admin: { email: string; name: string } | null;
  reason: string | null;
  ip: string | null;
  createdAt: string;
}
